import { RowState } from '@gcl/react-core/dist/components/Table/UnstyledTableComponents/Layout/VerticalTableLayout';
import { Dispatch, SetStateAction, useCallback } from 'react';
import { ComponentType, Icon, Loader, Tooltip } from '@gcl/react-core';
import { ApiSuccessToast } from '@core/ApiSuccessToast';
import { ApiErrorDisplay } from '@core/ApiErrorDisplay';
import { useQueryClient } from '@tanstack/react-query';
import { FETCH_QUERIES_NAME } from '@common/constants';
import { useAddProjectInterested, useDeleteProjectInterested } from './project.queries';
import { IProject } from './project.models';

interface IProjectListRowInterested {
  rowState: RowState | undefined;
  setRowState: Dispatch<SetStateAction<RowState>> | undefined;
  project: IProject;
}

export const ProjectListRowInterested = ({ rowState, setRowState, project }: IProjectListRowInterested) => {
  const queryClient = useQueryClient();

  const {
    mutate: mutateAddProjectInterested,
    isSuccess: isSuccessAddProjectInterested,
    error: errorAddProjectInterested,
    reset: resetAddProjectInterested,
  } = useAddProjectInterested();
  const {
    mutate: mutateDeleteProjectInterested,
    isSuccess: isSuccessDeleteProjectInterested,
    error: errorDeleteProjectInterested,
    reset: resetDeleteProjectInterested,
  } = useDeleteProjectInterested();

  const onClickPin = useCallback(() => {
    setRowState?.('Loading');
    if (project.isInterested) {
      mutateDeleteProjectInterested(project.id);
      return;
    }
    mutateAddProjectInterested(project.id);
  }, [JSON.stringify(project)]);

  return (
    <>
      <ApiSuccessToast
        successes={[
          {
            isSuccess: isSuccessAddProjectInterested,
            message: 'Pin project success',
            reset: resetAddProjectInterested,
          },
          {
            isSuccess: isSuccessDeleteProjectInterested,
            message: 'Unpin project success',
            reset: resetDeleteProjectInterested,
          },
        ]}
        callbackAfterToasted={() => {
          setRowState?.('Idle');
          queryClient.invalidateQueries([FETCH_QUERIES_NAME.PROJECT_SEARCH]);
        }}
      />
      <ApiErrorDisplay
        errors={[
          { error: errorAddProjectInterested, genericError: 'Pin project failed', reset: resetAddProjectInterested },
          {
            error: errorDeleteProjectInterested,
            genericError: 'Unpin project failed',
            reset: resetDeleteProjectInterested,
          },
        ]}
        callbackAfterToast={() => {
          setRowState?.('Idle');
        }}
      />
      {rowState === 'Loading' && <Loader small type={ComponentType.Primary} text="" />}
      {rowState === 'Idle' && (
        <Tooltip overlay={project.isInterested ? 'Unpin project' : 'Pin project'}>
          <Icon
            iconName={project.isInterested ? 'PinAngleFill' : 'PinAngle'}
            className={`${project.isInterested ? 'gcl-fc-danger-600' : 'gcl-fc-greyscale-500'}`}
            onClick={onClickPin}
          />
        </Tooltip>
      )}
    </>
  );
};
